import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  IconButton,
  Button,
  TextField,
  Divider,
  Chip,
  Alert,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import AppDialog from '../common/AppDialog';
import {
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  Warning as WarningIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { useShoppingCart } from '../../contexts/ShoppingCartContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { PaymentMethod, PaymentType, Sale } from '../../types';
import { formatCurrency } from '../../utils/currency';
import { calculateCartTotals } from '../../utils/salesPricing';
import { apiClient } from '../../services/api';
import { schedulePreview } from '../customer/CustomerPaymentPlans';

interface ShoppingCartProps {
  onSaleCompleted?: (sale: Sale) => void;
}

const isPositiveIntegerInput = (value: string): boolean => (
  /^[1-9]\d*$/.test(value) && Number.isSafeInteger(Number(value))
);

export function ShoppingCart({ onSaleCompleted }: ShoppingCartProps) {
  const { state, updateItemQuantity, removeItem, clearCart, validateStock, getCartTotal, getCartItemCount, completeSale } = useShoppingCart();
  const { t, language } = useLanguage();
  const [planAvailable, setPlanAvailable] = useState(false);
  const [installmentsEnabled, setInstallmentsEnabled] = useState(false);
  const [installmentCount, setInstallmentCount] = useState('6');
  const [firstDueDate, setFirstDueDate] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentType, setPaymentType] = useState<PaymentType>(PaymentType.FULL_PAID);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PaymentMethod.CASH);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [quantityDrafts, setQuantityDrafts] = useState<Record<string, string>>({});

  const cartTotal = getCartTotal();
  const pricingTotals = calculateCartTotals(state.cartItems);
  const itemCount = getCartItemCount();
  const numericPaymentAmount = Number(paymentAmount);
  const partialPaymentInvalid = paymentType === PaymentType.PARTIAL_PAID &&
    (!Number.isFinite(numericPaymentAmount) || numericPaymentAmount <= 0 || numericPaymentAmount >= cartTotal);
  const paymentInvalid = paymentType !== PaymentType.NO_PAYMENT &&
    (!Number.isFinite(numericPaymentAmount) || numericPaymentAmount <= 0 || numericPaymentAmount > cartTotal || partialPaymentInvalid);
  const outstanding = paymentType === PaymentType.NO_PAYMENT ? cartTotal : cartTotal - numericPaymentAmount;
  const planPreview = installmentsEnabled ? schedulePreview(outstanding.toFixed(2), installmentCount, firstDueDate) : [];

  useEffect(() => {
    if (!showCheckout || !state.selectedCustomer || !apiClient.paymentPlans) return;
    let active = true;
    apiClient.paymentPlans.list(state.selectedCustomer.id).then(() => { if (active) setPlanAvailable(true); })
      .catch(() => { if (active) setPlanAvailable(false); });
    return () => { active = false; };
  }, [showCheckout, state.selectedCustomer]);

  useEffect(() => {
    if (state.cartItems.length > 0) {
      validateStock();
    }
  }, [state.cartItems, validateStock]);

  useEffect(() => {
    setQuantityDrafts(previous => {
      const next: Record<string, string> = {};
      state.cartItems.forEach(item => {
        const key = `${item.itemId}-${item.batchId || 'no-batch'}-${item.unitPrice}`;
        next[key] = previous[key] ?? String(item.quantity);
      });
      return next;
    });
  }, [state.cartItems]);

  const handleQuantityChange = (itemId: string, newQuantity: number, batchId?: string, unitPrice?: number) => {
    if (!Number.isInteger(newQuantity) || newQuantity < 1) return;
    updateItemQuantity(itemId, newQuantity, batchId, unitPrice);
  };

  const handleQuantityDraftChange = (itemId: string, value: string, currentQuantity: number, batchId?: string, unitPrice?: number) => {
    const key = `${itemId}-${batchId || 'no-batch'}-${unitPrice}`;
    if (!/^\d*$/.test(value)) return;
    if (isPositiveIntegerInput(value)) {
      setQuantityDrafts(previous => ({ ...previous, [key]: value }));
      handleQuantityChange(itemId, Number(value), batchId, unitPrice);
    } else if (value === '') {
      // Keep the cart quantity while the user is editing the field.
      setQuantityDrafts(previous => ({ ...previous, [key]: value }));
    } else {
      setQuantityDrafts(previous => ({ ...previous, [key]: String(currentQuantity) }));
    }
  };

  const handleQuantityDraftBlur = (itemId: string, currentQuantity: number, batchId?: string, unitPrice?: number) => {
    const key = `${itemId}-${batchId || 'no-batch'}-${unitPrice}`;
    const draft = quantityDrafts[key] || '';
    if (!isPositiveIntegerInput(draft)) {
      setQuantityDrafts(previous => ({ ...previous, [key]: String(currentQuantity) }));
      return;
    }
    const normalized = String(Number(draft));
    setQuantityDrafts(previous => ({ ...previous, [key]: normalized }));
  };

  const handleRemoveItem = (itemId: string, batchId?: string, unitPrice?: number) => {
    removeItem(itemId, batchId, unitPrice);
  };

  const handleCheckout = () => {
    setPaymentAmount(cartTotal.toString());
    setShowCheckout(true);
  };

  const handleCompleteSale = async () => {
    if (paymentInvalid || (installmentsEnabled && planPreview.length === 0)) return;
    setIsProcessing(true);
    try {
      const args: [PaymentType, number, PaymentMethod | undefined, string | undefined] = [
        paymentType, paymentType !== PaymentType.NO_PAYMENT ? numericPaymentAmount : 0,
        paymentType !== PaymentType.NO_PAYMENT ? paymentMethod : undefined, paymentNotes || undefined,
      ];
      const completedSale = installmentsEnabled
        ? await completeSale(...args, { installmentCount: Number(installmentCount), firstDueDate })
        : await completeSale(...args);
      
      setShowCheckout(false);
      setPaymentAmount('');
      setPaymentNotes('');
      setPaymentType(PaymentType.FULL_PAID);
      setInstallmentsEnabled(false);
      
      if (onSaleCompleted) {
        onSaleCompleted(completedSale);
      }
    } catch {
      // ShoppingCartContext stores the mutation error for the cart alert.
    } finally {
      setIsProcessing(false);
    }
  };

  const getStockStatus = (itemId: string) => {
    const validation = state.stockValidation[itemId];
    if (!validation) return null;
    
    return validation.isValid ? 'available' : 'unavailable';
  };

  const getStockMessage = (itemId: string) => {
    const validation = state.stockValidation[itemId];
    return validation?.message || '';
  };

  if (state.cartItems.length === 0) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CartIcon color="primary" />{t('sales.shoppingCart')}</Typography>
          <Box display="flex" flexDirection="column" alignItems="center" py={5} textAlign="center">
            <CartIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: .55, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('sales.yourCartIsEmpty')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('sales.addItemsToStart')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CartIcon color="primary" />{t('sales.shoppingCart')}
              <Chip label={itemCount} size="small" color="primary" variant="outlined" />
            </Typography>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={clearCart}
              disabled={state.isLoading}
            >
              {t('sales.clearCart')}
            </Button>
          </Box>

          {state.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {state.error}
            </Alert>
          )}


          <List disablePadding>
            {state.cartItems.map((item, index) => {
              const stockStatus = getStockStatus(item.itemId);
              const stockMessage = getStockMessage(item.itemId);
              const quantityKey = `${item.itemId}-${item.batchId || 'no-batch'}-${item.unitPrice}`;
              const quantityDraft = quantityDrafts[quantityKey] ?? String(item.quantity);
              
              return (
                <React.Fragment key={`${item.itemId}-${item.batchId || 'no-batch'}-${item.unitPrice}`}>
                  <ListItem sx={{ display: 'block', px: 0, py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700}>{item.itemName}</Typography>
                        {item.batchNumber && <Chip label={`${t('sales.batch')}: ${item.batchNumber}`} size="small" variant="outlined" sx={{ mt: .5 }} />}
                        <Typography variant="body2" color="text.secondary">{formatCurrency(item.unitPrice)} × {item.quantity}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('sales.netAmount')}: {formatCurrency(item.netAmount)} · {t('sales.vatAmount')} (%{item.vatRate}): {formatCurrency(item.vatAmount)}
                        </Typography>
                      </Box>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(item.totalPrice)}</Typography>
                    </Box>
                    {stockStatus === 'unavailable' && <Box sx={{ display: 'flex', alignItems: 'center', gap: .5, color: 'warning.main', mt: .5 }}><WarningIcon fontSize="small" /><Typography variant="caption">{stockMessage}</Typography></Box>}
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="text.secondary">
                          {t('sales.quantity')}:
                        </Typography>
                        <TextField
                          size="small"
                          value={quantityDraft}
                          onChange={(e) => {
                            handleQuantityDraftChange(item.itemId, e.target.value, item.quantity, item.batchId, item.unitPrice);
                          }}
                          onBlur={() => handleQuantityDraftBlur(item.itemId, item.quantity, item.batchId, item.unitPrice)}
                          onKeyDown={(e) => {
                            if (['e', 'E', '+', '-', '.', ','].includes(e.key)) e.preventDefault();
                          }}
                          inputProps={{
                            min: 1,
                            step: 1,
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            'data-testid': `shopping-cart-quantity-${item.itemId}`,
                            style: { textAlign: 'center', width: '50px' }
                          }}
                          type="number"
                          sx={{
                            width: '70px',
                            '& input[type=number]': { MozAppearance: 'textfield' },
                            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0,
                            },
                          }}
                          disabled={state.isLoading}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveItem(item.itemId, item.batchId, item.unitPrice)}
                        disabled={state.isLoading}
                        aria-label={`${t('common.delete')}: ${item.itemName}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItem>
                  {index < state.cartItems.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ p: 2, mb: 2, borderRadius: 1.5, background: theme => `linear-gradient(125deg, ${theme.palette.action.selected}, ${theme.palette.background.paper})` }}>
            <Stack spacing={0.5}>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography variant="body2" color="text.secondary">{t('sales.netAmount')}</Typography>
                <Typography variant="body2">{formatCurrency(pricingTotals.netAmount)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Typography variant="body2" color="text.secondary">{t('sales.vatAmount')}</Typography>
                <Typography variant="body2">{formatCurrency(pricingTotals.vatAmount)}</Typography>
              </Box>
              <Divider />
              <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={2}>
                <Typography fontWeight={700}>{t('sales.grossAmount')}</Typography>
                <Typography variant="h5" color="primary.main" fontWeight={700}>{formatCurrency(cartTotal)}</Typography>
              </Box>
            </Stack>
          </Box>
          <Box>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<PaymentIcon />}
              data-testid="sales-checkout"
              onClick={handleCheckout}
              disabled={state.isLoading || cartTotal === 0 || 
                       Object.values(state.stockValidation).some(v => !v.isValid)}
              fullWidth
            >
              {t('sales.checkout')}
            </Button>
          </Box>

          {state.isLoading && (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress size={24} />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Checkout Dialog */}
      <AppDialog presentation="compact" open={showCheckout} onClose={() => setShowCheckout(false)} closeDisabled={state.isLoading} maxWidth="sm" fullWidth>
        <DialogTitle>{t('sales.completeSale')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('sales.orderSummary')}
              </Typography>
              <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: 'action.hover', display: 'flex', flexDirection: 'column', gap: .75 }}>
                <Typography variant="body2" color="text.secondary">{t('sales.customer')}: <strong>{state.selectedCustomer?.firstName} {state.selectedCustomer?.lastName}</strong></Typography>
                <Typography variant="body2" color="text.secondary">{t('sales.items')}: <strong>{itemCount}</strong></Typography>
                <Typography variant="body2" color="text.secondary">{t('sales.netAmount')}: <strong>{formatCurrency(pricingTotals.netAmount)}</strong></Typography>
                <Typography variant="body2" color="text.secondary">{t('sales.vatAmount')}: <strong>{formatCurrency(pricingTotals.vatAmount)}</strong></Typography>
                <Typography variant="h6" color="primary.main">{t('sales.grossAmount')}: {formatCurrency(cartTotal)}</Typography>
              </Box>
            </Box>
            
            <FormControl fullWidth>
              <InputLabel id="sales-payment-type-label">{t('sales.paymentType')}</InputLabel>
              <Select
                labelId="sales-payment-type-label"
                value={paymentType}
                inputProps={{ 'data-testid': 'sales-payment-type' }}
                onChange={(e) => {
                  const newPaymentType = e.target.value as PaymentType;
                  setPaymentType(newPaymentType);
                  if (newPaymentType === PaymentType.FULL_PAID) setInstallmentsEnabled(false);
                  if (newPaymentType === PaymentType.FULL_PAID) {
                    setPaymentAmount(cartTotal.toString());
                  } else if (newPaymentType === PaymentType.NO_PAYMENT) {
                    setPaymentAmount('0');
                  }
                }}
                label={t('sales.paymentType')}
              >
                <MenuItem value={PaymentType.FULL_PAID}>{t('sales.fullPaid')}</MenuItem>
                <MenuItem value={PaymentType.PARTIAL_PAID}>{t('sales.partialPaid')}</MenuItem>
                <MenuItem value={PaymentType.NO_PAYMENT}>{t('sales.noPayment')}</MenuItem>
              </Select>
            </FormControl>

            {paymentType !== PaymentType.NO_PAYMENT && (
              <>
            
                <TextField
                  fullWidth
                  label={t('sales.paymentAmount')}
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  disabled={paymentType === PaymentType.FULL_PAID}
                  error={paymentType === PaymentType.PARTIAL_PAID && partialPaymentInvalid}
                  helperText={
                    paymentType === PaymentType.PARTIAL_PAID
                      ? partialPaymentInvalid ? t('sales.partialPaymentRange') : t('sales.remainingAmountDebt')
                      : ''
                  }
                />
                
                <FormControl fullWidth>
                  <InputLabel id="sales-payment-method-label">{t('sales.paymentMethod')}</InputLabel>
                  <Select
                    labelId="sales-payment-method-label"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                    label={t('sales.paymentMethod')}
                  >
                    <MenuItem value={PaymentMethod.CASH}>{t('sales.cash')}</MenuItem>
                    <MenuItem value={PaymentMethod.CARD}>{t('sales.card')}</MenuItem>
                    <MenuItem value={PaymentMethod.BANK_TRANSFER}>{t('sales.bankTransfer')}</MenuItem>
                    <MenuItem value={PaymentMethod.CHECK}>{t('sales.check')}</MenuItem>
                    <MenuItem value={PaymentMethod.OTHER}>{t('sales.other')}</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            {paymentType === PaymentType.NO_PAYMENT && (
              <Alert severity="info">{t('sales.fullAmountDebt')}</Alert>
            )}
            {paymentType === PaymentType.PARTIAL_PAID && !partialPaymentInvalid && (
              <Alert severity="info">
                <Typography component="span" color="text.primary">{t('sales.remainingAmount')}: </Typography>
                <Typography component="span" color="error.main">{formatCurrency(cartTotal - numericPaymentAmount)}</Typography>
              </Alert>
            )}

            {planAvailable && paymentType === PaymentType.FULL_PAID && (
              <Alert severity="info">
                {language === 'tr'
                  ? 'Taksitli ödeme planı oluşturmak için Ödeme Türü alanından “Kısmi Ödendi” veya “Ödeme Yok” seçin.'
                  : 'To create an installment plan, select “Partially paid” or “No payment” from Payment type.'}
              </Alert>
            )}

            {planAvailable && paymentType !== PaymentType.FULL_PAID && <Box>
              <FormControlLabel control={<Checkbox checked={installmentsEnabled} onChange={e => setInstallmentsEnabled(e.target.checked)} />}
                label={language === 'tr' ? 'Kalan tutar için taksit planı oluştur' : 'Create an installment plan for the remaining balance'} />
              {installmentsEnabled && <Stack spacing={2}>
                <TextField type="number" label={language === 'tr' ? 'Taksit sayısı' : 'Installment count'} value={installmentCount} onChange={e => setInstallmentCount(e.target.value)} inputProps={{ min: 1, max: 120 }} />
                <TextField type="date" label={language === 'tr' ? 'İlk vade' : 'First due date'} InputLabelProps={{ shrink: true }} value={firstDueDate} onChange={e => setFirstDueDate(e.target.value)} />
                <Box sx={{ maxHeight: 140, overflow: 'auto' }}>{planPreview.map((item, i) => <Typography variant="body2" key={i}>{i + 1}. {item.date} — {formatCurrency(item.cents / 100)}</Typography>)}</Box>
              </Stack>}
            </Box>}
            
            <TextField
              fullWidth
              label={t('sales.paymentNotes')}
              multiline
              rows={2}
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCheckout(false)} disabled={isProcessing}>
            {t('sales.cancel')}
          </Button>
          <Button
            onClick={handleCompleteSale}
            variant="contained"
            data-testid="sales-complete"
            disabled={isProcessing || paymentInvalid || (installmentsEnabled && planPreview.length === 0)}
            startIcon={isProcessing ? <CircularProgress size={20} /> : <PaymentIcon />}
          >
            {isProcessing ? t('sales.processing') : t('sales.completeSale')}
          </Button>
        </DialogActions>
      </AppDialog>
    </Box>
  );
}
